import React, { useState } from 'react';
import { BookOpen, CheckCircle, ChevronRight, Clock, Download, Edit, FileText, Film, Plus, Settings, Trash2, Video, X, ExternalLink, Presentation, FileSpreadsheet, FileCode, MessageSquare, MoreHorizontal, Circle, GripVertical, Users, Search, BarChart, Target, Compass, Map, Lightbulb, Zap, PieChart, LineChart, UserCheck, Rocket, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
interface ProgramProps {
  userRole: 'Program Manager' | 'Founder';
}
// Types
type ModuleStatus = 'completed' | 'in-progress' | 'not-started';
type PhaseType = 'Problem' | 'Position' | 'Pilot' | 'Resources';
type TemplateType = 'miro' | 'powerpoint' | 'word' | 'excel' | 'agent';
type ModuleIconType = 'search' | 'users' | 'target' | 'chart' | 'compass' | 'map' | 'lightbulb' | 'zap' | 'globe' | 'rocket';
interface Template {
  id: string;
  name: string;
  type: TemplateType;
  url: string;
}
interface Module {
  id: string;
  title: string;
  description: string;
  status: ModuleStatus;
  videoUrl: string;
  transcriptUrl: string;
  slidesUrl: string;
  templates: Template[];
  order: number;
  iconType?: ModuleIconType;
}
interface Phase {
  id: string;
  type: PhaseType;
  title: string;
  description: string;
  modules: Module[];
}
// Create a sortable item component for the module number
const DraggableModuleNumber = ({
  moduleId,
  order,
  userRole
}: {
  moduleId: string;
  order: number;
  userRole: 'Program Manager' | 'Founder';
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: moduleId
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: userRole === 'Program Manager' ? 'grab' : 'default',
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center'
  };
  return <span ref={setNodeRef} style={style} className="text-gray-400 text-sm flex items-center" {...userRole === 'Program Manager' ? {
    ...attributes,
    ...listeners
  } : {}} data-id="element-1066">
      {userRole === 'Program Manager' && <GripVertical size={12} className="mr-1 text-gray-400" data-id="element-1067" />}
      #{order}
    </span>;
};
const Program: React.FC<ProgramProps> = ({
  userRole
}) => {
  // State
  const [activePhase, setActivePhase] = useState<PhaseType>('Problem');
  const [isAddModuleModalOpen, setIsAddModuleModalOpen] = useState(false);
  const [isEditModuleModalOpen, setIsEditModuleModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);
  const [isEditTemplateModalOpen, setIsEditTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  // Add sensors for drag and drop
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      const activePhaseObj = phases.find(phase => phase.type === activePhase);
      if (!activePhaseObj) return;
      const updatedPhases = [...phases];
      const phaseIndex = updatedPhases.findIndex(phase => phase.type === activePhase);
      // Find the indices of the modules being reordered
      const oldIndex = activePhaseObj.modules.findIndex(module => module.id === active.id);
      const newIndex = activePhaseObj.modules.findIndex(module => module.id === over.id);
      // Reorder the modules array
      updatedPhases[phaseIndex].modules = arrayMove(updatedPhases[phaseIndex].modules, oldIndex, newIndex);
      // Update the order property of each module
      updatedPhases[phaseIndex].modules = updatedPhases[phaseIndex].modules.map((module, index) => ({
        ...module,
        order: index + 1
      }));
      setPhases(updatedPhases);
    }
  };
  // Mock data for the program phases and modules
  const [phases, setPhases] = useState<Phase[]>([{
    id: 'phase-1',
    type: 'Problem',
    title: 'Problem',
    description: 'Identify and validate customer problems worth solving',
    modules: [{
      id: 'module-1-1',
      title: 'Problem Framing',
      description: 'Learn how to identify and frame problems in a way that leads to innovative solutions.',
      status: 'completed',
      videoUrl: 'https://example.com/video1',
      transcriptUrl: 'https://example.com/transcript1',
      slidesUrl: 'https://example.com/slides1',
      templates: [{
        id: 't1',
        name: 'Problem Canvas',
        type: 'miro',
        url: 'https://miro.com/app/board/template1'
      }, {
        id: 't2',
        name: 'Problem Statement Guide',
        type: 'powerpoint',
        url: 'https://example.com/ppt1'
      }, {
        id: 't3',
        name: 'Problem Analysis Tool',
        type: 'excel',
        url: 'https://example.com/excel1'
      }, {
        id: 't4',
        name: 'Problem Framing Agent',
        type: 'agent',
        url: '/agents/problem-framing'
      }],
      order: 1
    }, {
      id: 'module-1-2',
      title: 'Customer Discovery',
      description: 'Learn techniques for effective customer interviews to uncover real problems.',
      status: 'completed',
      videoUrl: 'https://example.com/video2',
      transcriptUrl: 'https://example.com/transcript2',
      slidesUrl: 'https://example.com/slides2',
      templates: [{
        id: 't5',
        name: 'Interview Guide',
        type: 'word',
        url: 'https://example.com/word1'
      }, {
        id: 't6',
        name: 'Customer Segmentation',
        type: 'excel',
        url: 'https://example.com/excel2'
      }, {
        id: 't7',
        name: 'Interview Analysis Agent',
        type: 'agent',
        url: '/agents/interview-analysis'
      }],
      order: 2
    }, {
      id: 'module-1-3',
      title: 'Problem Validation',
      description: 'Validate that your identified problem is worth solving through market analysis.',
      status: 'in-progress',
      videoUrl: 'https://example.com/video3',
      transcriptUrl: 'https://example.com/transcript3',
      slidesUrl: 'https://example.com/slides3',
      templates: [{
        id: 't8',
        name: 'Validation Framework',
        type: 'miro',
        url: 'https://miro.com/app/board/template3'
      }, {
        id: 't9',
        name: 'Market Analysis Template',
        type: 'excel',
        url: 'https://example.com/excel3'
      }],
      order: 3
    },
    // Adding more modules to reach 10
    {
      id: 'module-1-4',
      title: 'Market Sizing',
      description: 'Learn how to estimate the size of your target market and opportunity.',
      status: 'not-started',
      videoUrl: 'https://example.com/video4',
      transcriptUrl: 'https://example.com/transcript4',
      slidesUrl: 'https://example.com/slides4',
      templates: [{
        id: 't10',
        name: 'Market Sizing Tool',
        type: 'excel',
        url: 'https://example.com/excel4'
      }, {
        id: 't11',
        name: 'TAM SAM SOM Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt2'
      }],
      order: 4
    }, {
      id: 'module-1-5',
      title: 'Competitive Analysis',
      description: 'Map the competitive landscape to identify gaps and opportunities.',
      status: 'not-started',
      videoUrl: 'https://example.com/video5',
      transcriptUrl: 'https://example.com/transcript5',
      slidesUrl: 'https://example.com/slides5',
      templates: [{
        id: 't12',
        name: 'Competitive Matrix',
        type: 'excel',
        url: 'https://example.com/excel5'
      }, {
        id: 't13',
        name: 'Competitive Analysis Framework',
        type: 'miro',
        url: 'https://miro.com/app/board/template4'
      }],
      order: 5
    }, {
      id: 'module-1-6',
      title: 'Problem-Solution Fit',
      description: 'Evaluate if your solution truly addresses the identified problem.',
      status: 'not-started',
      videoUrl: 'https://example.com/video6',
      transcriptUrl: 'https://example.com/transcript6',
      slidesUrl: 'https://example.com/slides6',
      templates: [{
        id: 't14',
        name: 'Problem-Solution Canvas',
        type: 'miro',
        url: 'https://miro.com/app/board/template5'
      }, {
        id: 't15',
        name: 'Fit Analysis Tool',
        type: 'excel',
        url: 'https://example.com/excel6'
      }],
      order: 6
    }, {
      id: 'module-1-7',
      title: 'User Personas',
      description: 'Create detailed user personas to guide product development.',
      status: 'not-started',
      videoUrl: 'https://example.com/video7',
      transcriptUrl: 'https://example.com/transcript7',
      slidesUrl: 'https://example.com/slides7',
      templates: [{
        id: 't16',
        name: 'Persona Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt3'
      }, {
        id: 't17',
        name: 'User Interview Guide',
        type: 'word',
        url: 'https://example.com/word2'
      }],
      order: 7
    }, {
      id: 'module-1-8',
      title: 'Customer Journey Mapping',
      description: 'Map the customer journey to identify pain points and opportunities.',
      status: 'not-started',
      videoUrl: 'https://example.com/video8',
      transcriptUrl: 'https://example.com/transcript8',
      slidesUrl: 'https://example.com/slides8',
      templates: [{
        id: 't18',
        name: 'Journey Map Template',
        type: 'miro',
        url: 'https://miro.com/app/board/template6'
      }, {
        id: 't19',
        name: 'Journey Analysis Tool',
        type: 'excel',
        url: 'https://example.com/excel7'
      }],
      order: 8
    }, {
      id: 'module-1-9',
      title: 'Problem Statement Refinement',
      description: 'Refine your problem statement based on research and validation.',
      status: 'not-started',
      videoUrl: 'https://example.com/video9',
      transcriptUrl: 'https://example.com/transcript9',
      slidesUrl: 'https://example.com/slides9',
      templates: [{
        id: 't20',
        name: 'Problem Refinement Framework',
        type: 'word',
        url: 'https://example.com/word3'
      }, {
        id: 't21',
        name: 'Problem Statement Generator',
        type: 'agent',
        url: '/agents/problem-statement'
      }],
      order: 9
    }, {
      id: 'module-1-10',
      title: 'Problem Phase Summary',
      description: 'Summarize learnings and prepare for the Position phase.',
      status: 'not-started',
      videoUrl: 'https://example.com/video10',
      transcriptUrl: 'https://example.com/transcript10',
      slidesUrl: 'https://example.com/slides10',
      templates: [{
        id: 't22',
        name: 'Phase Summary Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt4'
      }, {
        id: 't23',
        name: 'Problem Phase Checklist',
        type: 'excel',
        url: 'https://example.com/excel8'
      }],
      order: 10
    }]
  }, {
    id: 'phase-2',
    type: 'Position',
    title: 'Position',
    description: 'Define your unique value proposition and market position',
    modules: [{
      id: 'module-2-1',
      title: 'Value Proposition Design',
      description: 'Craft a compelling value proposition that resonates with your target audience.',
      status: 'not-started',
      videoUrl: 'https://example.com/video11',
      transcriptUrl: 'https://example.com/transcript11',
      slidesUrl: 'https://example.com/slides11',
      templates: [{
        id: 't24',
        name: 'Value Prop Canvas',
        type: 'miro',
        url: 'https://miro.com/app/board/template7'
      }, {
        id: 't25',
        name: 'Value Proposition Builder',
        type: 'agent',
        url: '/agents/value-prop-builder'
      }],
      order: 1
    },
    // Adding 9 more modules for Position phase to reach 10
    {
      id: 'module-2-2',
      title: 'Market Positioning Strategy',
      description: 'Develop a positioning strategy that differentiates your solution in the market.',
      status: 'not-started',
      videoUrl: 'https://example.com/video12',
      transcriptUrl: 'https://example.com/transcript12',
      slidesUrl: 'https://example.com/slides12',
      templates: [{
        id: 't26',
        name: 'Positioning Map',
        type: 'miro',
        url: 'https://miro.com/app/board/template8'
      }, {
        id: 't27',
        name: 'Positioning Statement Template',
        type: 'word',
        url: 'https://example.com/word4'
      }],
      order: 2
    }, {
      id: 'module-2-3',
      title: 'Brand Development',
      description: 'Build a brand identity that communicates your value and resonates with customers.',
      status: 'not-started',
      videoUrl: 'https://example.com/video13',
      transcriptUrl: 'https://example.com/transcript13',
      slidesUrl: 'https://example.com/slides13',
      templates: [{
        id: 't28',
        name: 'Brand Guidelines Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt5'
      }, {
        id: 't29',
        name: 'Brand Asset Library',
        type: 'miro',
        url: 'https://miro.com/app/board/template9'
      }],
      order: 3
    }, {
      id: 'module-2-4',
      title: 'Messaging Framework',
      description: 'Create consistent messaging that communicates your value to different audiences.',
      status: 'not-started',
      videoUrl: 'https://example.com/video14',
      transcriptUrl: 'https://example.com/transcript14',
      slidesUrl: 'https://example.com/slides14',
      templates: [{
        id: 't30',
        name: 'Messaging Matrix',
        type: 'excel',
        url: 'https://example.com/excel9'
      }, {
        id: 't31',
        name: 'Messaging Generator',
        type: 'agent',
        url: '/agents/messaging-generator'
      }],
      order: 4
    }, {
      id: 'module-2-5',
      title: 'Go-to-Market Strategy',
      description: 'Plan your market entry strategy and initial customer acquisition approach.',
      status: 'not-started',
      videoUrl: 'https://example.com/video15',
      transcriptUrl: 'https://example.com/transcript15',
      slidesUrl: 'https://example.com/slides15',
      templates: [{
        id: 't32',
        name: 'GTM Strategy Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt6'
      }, {
        id: 't33',
        name: 'Channel Strategy Planner',
        type: 'excel',
        url: 'https://example.com/excel10'
      }],
      order: 5
    }, {
      id: 'module-2-6',
      title: 'Pricing Strategy',
      description: 'Develop a pricing strategy that captures value and drives adoption.',
      status: 'not-started',
      videoUrl: 'https://example.com/video16',
      transcriptUrl: 'https://example.com/transcript16',
      slidesUrl: 'https://example.com/slides16',
      templates: [{
        id: 't34',
        name: 'Pricing Model Calculator',
        type: 'excel',
        url: 'https://example.com/excel11'
      }, {
        id: 't35',
        name: 'Pricing Strategy Framework',
        type: 'miro',
        url: 'https://miro.com/app/board/template10'
      }],
      order: 6
    }, {
      id: 'module-2-7',
      title: 'Customer Acquisition Planning',
      description: 'Develop strategies to acquire your first customers efficiently.',
      status: 'not-started',
      videoUrl: 'https://example.com/video17',
      transcriptUrl: 'https://example.com/transcript17',
      slidesUrl: 'https://example.com/slides17',
      templates: [{
        id: 't36',
        name: 'CAC Calculator',
        type: 'excel',
        url: 'https://example.com/excel12'
      }, {
        id: 't37',
        name: 'Acquisition Channel Map',
        type: 'miro',
        url: 'https://miro.com/app/board/template11'
      }],
      order: 7
    }, {
      id: 'module-2-8',
      title: 'Sales Strategy Development',
      description: 'Create a sales approach that effectively communicates your value to prospects.',
      status: 'not-started',
      videoUrl: 'https://example.com/video18',
      transcriptUrl: 'https://example.com/transcript18',
      slidesUrl: 'https://example.com/slides18',
      templates: [{
        id: 't38',
        name: 'Sales Playbook Template',
        type: 'word',
        url: 'https://example.com/word5'
      }, {
        id: 't39',
        name: 'Sales Process Map',
        type: 'miro',
        url: 'https://miro.com/app/board/template12'
      }],
      order: 8
    }, {
      id: 'module-2-9',
      title: 'Competitive Positioning',
      description: 'Refine your positioning against competitors to highlight your unique advantages.',
      status: 'not-started',
      videoUrl: 'https://example.com/video19',
      transcriptUrl: 'https://example.com/transcript19',
      slidesUrl: 'https://example.com/slides19',
      templates: [{
        id: 't40',
        name: 'Competitive Analysis Matrix',
        type: 'excel',
        url: 'https://example.com/excel13'
      }, {
        id: 't41',
        name: 'Positioning Map',
        type: 'miro',
        url: 'https://miro.com/app/board/template13'
      }],
      order: 9
    }, {
      id: 'module-2-10',
      title: 'Position Phase Summary',
      description: 'Summarize your positioning work and prepare for the Pilot phase.',
      status: 'not-started',
      videoUrl: 'https://example.com/video20',
      transcriptUrl: 'https://example.com/transcript20',
      slidesUrl: 'https://example.com/slides20',
      templates: [{
        id: 't42',
        name: 'Positioning Summary Deck',
        type: 'powerpoint',
        url: 'https://example.com/ppt7'
      }, {
        id: 't43',
        name: 'Position Phase Checklist',
        type: 'excel',
        url: 'https://example.com/excel14'
      }],
      order: 10
    }]
  }, {
    id: 'phase-3',
    type: 'Pilot',
    title: 'Pilot',
    description: 'Launch and validate your solution with real customers',
    modules: [{
      id: 'module-3-1',
      title: 'Pilot Planning',
      description: 'Design an effective pilot program to test your solution with real users.',
      status: 'not-started',
      videoUrl: 'https://example.com/video21',
      transcriptUrl: 'https://example.com/transcript21',
      slidesUrl: 'https://example.com/slides21',
      templates: [{
        id: 't44',
        name: 'Pilot Planning Template',
        type: 'miro',
        url: 'https://miro.com/app/board/template14'
      }, {
        id: 't45',
        name: 'Pilot Timeline',
        type: 'excel',
        url: 'https://example.com/excel15'
      }],
      order: 1
    },
    // Adding 9 more modules for Pilot phase to reach 10
    {
      id: 'module-3-2',
      title: 'MVP Definition',
      description: 'Define the minimum viable product for your pilot program.',
      status: 'not-started',
      videoUrl: 'https://example.com/video22',
      transcriptUrl: 'https://example.com/transcript22',
      slidesUrl: 'https://example.com/slides22',
      templates: [{
        id: 't46',
        name: 'MVP Scope Template',
        type: 'miro',
        url: 'https://miro.com/app/board/template15'
      }, {
        id: 't47',
        name: 'Feature Prioritization Matrix',
        type: 'excel',
        url: 'https://example.com/excel16'
      }],
      order: 2
    }, {
      id: 'module-3-3',
      title: 'Pilot Customer Selection',
      description: 'Identify and recruit the right customers for your pilot program.',
      status: 'not-started',
      videoUrl: 'https://example.com/video23',
      transcriptUrl: 'https://example.com/transcript23',
      slidesUrl: 'https://example.com/slides23',
      templates: [{
        id: 't48',
        name: 'Customer Selection Criteria',
        type: 'word',
        url: 'https://example.com/word6'
      }, {
        id: 't49',
        name: 'Pilot Outreach Templates',
        type: 'word',
        url: 'https://example.com/word7'
      }],
      order: 3
    }, {
      id: 'module-3-4',
      title: 'Success Metrics Definition',
      description: 'Define clear metrics to evaluate the success of your pilot.',
      status: 'not-started',
      videoUrl: 'https://example.com/video24',
      transcriptUrl: 'https://example.com/transcript24',
      slidesUrl: 'https://example.com/slides24',
      templates: [{
        id: 't50',
        name: 'Metrics Framework',
        type: 'excel',
        url: 'https://example.com/excel17'
      }, {
        id: 't51',
        name: 'KPI Dashboard Template',
        type: 'excel',
        url: 'https://example.com/excel18'
      }],
      order: 4
    }, {
      id: 'module-3-5',
      title: 'Feedback Collection Planning',
      description: 'Design a process to collect and analyze user feedback during the pilot.',
      status: 'not-started',
      videoUrl: 'https://example.com/video25',
      transcriptUrl: 'https://example.com/transcript25',
      slidesUrl: 'https://example.com/slides25',
      templates: [{
        id: 't52',
        name: 'Feedback Collection Tools',
        type: 'miro',
        url: 'https://miro.com/app/board/template16'
      }, {
        id: 't53',
        name: 'User Interview Guide',
        type: 'word',
        url: 'https://example.com/word8'
      }],
      order: 5
    }, {
      id: 'module-3-6',
      title: 'Pilot Launch Preparation',
      description: 'Prepare all necessary materials and processes for your pilot launch.',
      status: 'not-started',
      videoUrl: 'https://example.com/video26',
      transcriptUrl: 'https://example.com/transcript26',
      slidesUrl: 'https://example.com/slides26',
      templates: [{
        id: 't54',
        name: 'Launch Checklist',
        type: 'excel',
        url: 'https://example.com/excel19'
      }, {
        id: 't55',
        name: 'Pilot Communication Plan',
        type: 'word',
        url: 'https://example.com/word9'
      }],
      order: 6
    }, {
      id: 'module-3-7',
      title: 'Pilot Execution',
      description: 'Effectively execute your pilot program with selected customers.',
      status: 'not-started',
      videoUrl: 'https://example.com/video27',
      transcriptUrl: 'https://example.com/transcript27',
      slidesUrl: 'https://example.com/slides27',
      templates: [{
        id: 't56',
        name: 'Pilot Management Dashboard',
        type: 'excel',
        url: 'https://example.com/excel20'
      }, {
        id: 't57',
        name: 'Issue Tracking Template',
        type: 'excel',
        url: 'https://example.com/excel21'
      }],
      order: 7
    }, {
      id: 'module-3-8',
      title: 'Pilot Data Analysis',
      description: 'Analyze pilot data to extract insights and inform next steps.',
      status: 'not-started',
      videoUrl: 'https://example.com/video28',
      transcriptUrl: 'https://example.com/transcript28',
      slidesUrl: 'https://example.com/slides28',
      templates: [{
        id: 't58',
        name: 'Data Analysis Framework',
        type: 'excel',
        url: 'https://example.com/excel22'
      }, {
        id: 't59',
        name: 'Insights Generator',
        type: 'agent',
        url: '/agents/insights-generator'
      }],
      order: 8
    }, {
      id: 'module-3-9',
      title: 'Pilot Iteration Planning',
      description: 'Plan iterations based on pilot feedback and results.',
      status: 'not-started',
      videoUrl: 'https://example.com/video29',
      transcriptUrl: 'https://example.com/transcript29',
      slidesUrl: 'https://example.com/slides29',
      templates: [{
        id: 't60',
        name: 'Iteration Planning Board',
        type: 'miro',
        url: 'https://miro.com/app/board/template17'
      }, {
        id: 't61',
        name: 'Feature Roadmap Template',
        type: 'excel',
        url: 'https://example.com/excel23'
      }],
      order: 9
    }, {
      id: 'module-3-10',
      title: 'Program Completion & Next Steps',
      description: 'Wrap up the program and plan your path forward.',
      status: 'not-started',
      videoUrl: 'https://example.com/video30',
      transcriptUrl: 'https://example.com/transcript30',
      slidesUrl: 'https://example.com/slides30',
      templates: [{
        id: 't62',
        name: 'Program Summary Template',
        type: 'powerpoint',
        url: 'https://example.com/ppt8'
      }, {
        id: 't63',
        name: 'Growth Planning Framework',
        type: 'miro',
        url: 'https://miro.com/app/board/template18'
      }],
      order: 10
    }]
  }]);
  // Handler functions
  const handleAddModule = (phaseId: string) => {
    // Implementation for adding a new module
    setIsAddModuleModalOpen(true);
  };
  const handleEditModule = (module: Module) => {
    setSelectedModule(module);
    setIsEditModuleModalOpen(true);
  };
  const handleDeleteModule = (phaseId: string, moduleId: string) => {
    // Implementation for deleting a module
    const updatedPhases = phases.map(phase => {
      if (phase.id === phaseId) {
        return {
          ...phase,
          modules: phase.modules.filter(module => module.id !== moduleId)
        };
      }
      return phase;
    });
    setPhases(updatedPhases);
  };
  const handleDeleteTemplate = (moduleId: string, templateId: string) => {
    // Implementation for deleting a template
    const updatedPhases = phases.map(phase => {
      const updatedModules = phase.modules.map(module => {
        if (module.id === moduleId) {
          return {
            ...module,
            templates: module.templates.filter(template => template.id !== templateId)
          };
        }
        return module;
      });
      return {
        ...phase,
        modules: updatedModules
      };
    });
    setPhases(updatedPhases);
  };
  const handleToggleModuleExpansion = (moduleId: string) => {
    if (expandedModuleId === moduleId) {
      setExpandedModuleId(null);
    } else {
      setExpandedModuleId(moduleId);
    }
  };
  const getModuleStatusIcon = (status: ModuleStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} className="text-green-500" data-id="element-1068" />;
      case 'in-progress':
        return <Clock size={18} className="text-amber-500" data-id="element-1069" />;
      case 'not-started':
        return <Circle size={18} className="text-gray-300" data-id="element-1070" />;
    }
  };
  const getTemplateIcon = (type: TemplateType) => {
    switch (type) {
      case 'miro':
        return <Presentation size={16} className="text-blue-500" data-id="element-1071" />;
      case 'powerpoint':
        return <Presentation size={16} className="text-orange-500" data-id="element-1072" />;
      case 'word':
        return <FileText size={16} className="text-blue-700" data-id="element-1073" />;
      case 'excel':
        return <FileSpreadsheet size={16} className="text-green-600" data-id="element-1074" />;
      case 'agent':
        return <MessageSquare size={16} className="text-purple-600" data-id="element-1075" />;
    }
  };
  const getPhaseCompletion = (phase: Phase): {
    completed: number;
    total: number;
  } => {
    const completed = phase.modules.filter(module => module.status === 'completed').length;
    return {
      completed,
      total: phase.modules.length
    };
  };
  // Function to get the module icon based on iconType
  const getModuleIcon = (module: Module) => {
    // If module doesn't have an iconType yet, assign one based on id or title
    const iconType = module.iconType || getDefaultIconType(module);
    switch (iconType) {
      case 'search':
        return <Search size={18} className="text-blue-500" data-id="element-1076" />;
      case 'users':
        return <Users size={18} className="text-purple-500" data-id="element-1077" />;
      case 'target':
        return <Target size={18} className="text-red-500" data-id="element-1078" />;
      case 'chart':
        return <BarChart size={18} className="text-green-500" data-id="element-1079" />;
      case 'compass':
        return <Compass size={18} className="text-amber-500" data-id="element-1080" />;
      case 'map':
        return <Map size={18} className="text-indigo-500" data-id="element-1081" />;
      case 'lightbulb':
        return <Lightbulb size={18} className="text-yellow-500" data-id="element-1082" />;
      case 'zap':
        return <Zap size={18} className="text-orange-500" data-id="element-1083" />;
      case 'globe':
        return <Globe size={18} className="text-teal-500" data-id="element-1084" />;
      case 'rocket':
        return <Rocket size={18} className="text-pink-500" data-id="element-1085" />;
      default:
        return <BookOpen size={18} className="text-blue-500" data-id="element-1086" />;
    }
  };
  // Helper function to assign default icon types based on module content
  const getDefaultIconType = (module: Module): ModuleIconType => {
    const title = module.title.toLowerCase();
    if (title.includes('problem') || title.includes('framing')) return 'lightbulb';
    if (title.includes('customer') || title.includes('user') || title.includes('persona')) return 'users';
    if (title.includes('discovery') || title.includes('research')) return 'search';
    if (title.includes('validation') || title.includes('analysis')) return 'target';
    if (title.includes('market') || title.includes('sizing')) return 'chart';
    if (title.includes('competitive') || title.includes('position')) return 'compass';
    if (title.includes('journey') || title.includes('mapping')) return 'map';
    if (title.includes('solution') || title.includes('fit')) return 'zap';
    if (title.includes('global') || title.includes('world')) return 'globe';
    if (title.includes('launch') || title.includes('pilot') || title.includes('execution')) return 'rocket';
    // Default based on module order
    const icons: ModuleIconType[] = ['lightbulb', 'search', 'target', 'chart', 'compass', 'map', 'zap', 'users', 'globe', 'rocket'];
    return icons[module.order % icons.length];
  };
  // Find the active phase object
  const currentPhase = phases.find(phase => phase.type === activePhase) || phases[0];
  return <div className="w-full h-full bg-gray-50" data-id="element-1087">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-1088">
        <div className="flex items-center justify-between" data-id="element-1089">
          <div data-id="element-1090">
            <h1 className="text-xl font-semibold text-gray-800" data-id="element-1091">
              Program Journey
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1092">
              Design learning modules to help ventures progress through your
              program
            </p>
          </div>
          {userRole === 'Program Manager' && <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center transition-colors" data-id="element-1093">
              <Settings size={16} className="mr-2" data-id="element-1094" /> Program Settings
            </button>}
        </div>
      </div>
      {/* Phase Navigation */}
      <div className="bg-white border-b border-gray-200 px-6" data-id="element-1095">
        <div className="flex items-center space-x-1 py-2 px-auto justify-center" data-id="element-1096">
          {phases.map((phase, index) => {
          const {
            completed,
            total
          } = getPhaseCompletion(phase);
          return <div key={phase.id} className="flex items-center py-2" data-id="element-1097">
                <button onClick={() => setActivePhase(phase.type)} className={`relative flex flex-col items-center ${activePhase === phase.type ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1098">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 border-2 ${activePhase === phase.type ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'}`} data-id="element-1099">
                    <span className="font-semibold" data-id="element-1100">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-1101">{phase.title}</span>
                </button>
                {/* Connector line between phases */}
                {index < phases.length - 1 && <div className="h-0.5 w-16 bg-gray-300 mx-2" data-id="element-1102"></div>}
              </div>;
        })}
          {/* Add Phase button - only visible to Program Managers */}
          {userRole === 'Program Manager' && <div className="flex items-center py-2" data-id="element-1103">
              {/* Connector line before add button */}
              <div className="h-0.5 w-16 bg-gray-300 mx-2" data-id="element-1104"></div>
              <button onClick={() => alert('Add new phase')} className="relative flex flex-col items-center text-gray-500 hover:text-gray-700" data-id="element-1105">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-1 border-2 border-dashed border-gray-300 bg-gray-50" data-id="element-1106">
                  <Plus size={16} data-id="element-1107" />
                </div>
                <span className="text-sm font-medium" data-id="element-1108">Add new</span>
              </button>
            </div>}
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6" data-id="element-1109">
        <div className="mb-6" data-id="element-1110">
          <div className="flex items-center justify-between" data-id="element-1111">
            <div data-id="element-1112">
              <h2 className="text-lg font-semibold text-gray-800" data-id="element-1113">
                {currentPhase.title}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-1114">
                {currentPhase.description}
              </p>
            </div>
          </div>
        </div>
        {/* Module Grid */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} data-id="element-1115">
          <SortableContext items={currentPhase.modules.map(module => module.id)} strategy={verticalListSortingStrategy} data-id="element-1116">
            <div className="grid grid-cols-1 gap-4" data-id="element-1117">
              {currentPhase.modules.sort((a, b) => a.order - b.order).map(module => <div key={module.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-id="element-1118">
                    <div className="p-4 cursor-pointer" onClick={() => handleToggleModuleExpansion(module.id)} data-id="element-1119">
                      <div className="flex items-center justify-between" data-id="element-1120">
                        <div className="flex items-center space-x-3" data-id="element-1121">
                          {getModuleIcon(module)}
                          <DraggableModuleNumber moduleId={module.id} order={module.order} userRole={userRole} data-id="element-1122" />
                          <h3 className="font-medium text-gray-800" data-id="element-1123">
                            {module.title}
                          </h3>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-1124">
                          {userRole === 'Program Manager' && <div className="flex items-center space-x-1 mr-2" data-id="element-1125">
                              <button onClick={e => {
                        e.stopPropagation();
                        handleEditModule(module);
                      }} className="p-1 text-gray-500 hover:text-blue-600 rounded-md hover:bg-gray-100" data-id="element-1126">
                                <Edit size={16} data-id="element-1127" />
                              </button>
                            </div>}
                          <ChevronRight size={20} className={`text-gray-400 transition-transform ${expandedModuleId === module.id ? 'transform rotate-90' : ''}`} data-id="element-1128" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 ml-9" data-id="element-1129">
                        {module.description}
                      </p>
                    </div>
                    {/* Expanded Content */}
                    {expandedModuleId === module.id && <div className="border-t border-gray-200 p-4 bg-gray-50" data-id="element-1130">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1131">
                          {/* Left Column - Resources */}
                          <div data-id="element-1132">
                            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1133">
                              Learning Resources
                            </h4>
                            <div className="space-y-3" data-id="element-1134">
                              <a href={module.videoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:shadow-sm" data-id="element-1135">
                                <Video size={20} className="text-red-500 mr-3" data-id="element-1136" />
                                <div data-id="element-1137">
                                  <div className="text-sm font-medium text-gray-800" data-id="element-1138">
                                    Watch Video
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-1139">
                                    Learn the key concepts
                                  </div>
                                </div>
                                <div className="ml-auto flex items-center" data-id="element-1140">
                                  {userRole === 'Program Manager' && <>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle edit functionality here
                              alert('Edit video URL: ' + module.videoUrl);
                            }} className="mr-2 text-gray-400 hover:text-blue-500" aria-label="Edit video URL" data-id="element-1141">
                                        <Edit size={16} data-id="element-1142" />
                                      </button>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle delete functionality here
                              if (confirm('Are you sure you want to delete this video resource?')) {
                                alert('Video resource deleted');
                              }
                            }} className="mr-2 text-gray-400 hover:text-red-500" aria-label="Delete video resource" data-id="element-1143">
                                        <Trash2 size={16} data-id="element-1144" />
                                      </button>
                                    </>}
                                  <ExternalLink size={16} className="text-gray-400" data-id="element-1145" />
                                </div>
                              </a>
                              <a href={module.transcriptUrl} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:shadow-sm" data-id="element-1146">
                                <FileText size={20} className="text-blue-500 mr-3" data-id="element-1147" />
                                <div data-id="element-1148">
                                  <div className="text-sm font-medium text-gray-800" data-id="element-1149">
                                    Read Transcript
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-1150">
                                    Full text version of the video
                                  </div>
                                </div>
                                <div className="ml-auto flex items-center" data-id="element-1151">
                                  {userRole === 'Program Manager' && <>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle edit functionality here
                              alert('Edit transcript URL: ' + module.transcriptUrl);
                            }} className="mr-2 text-gray-400 hover:text-blue-500" aria-label="Edit transcript URL" data-id="element-1152">
                                        <Edit size={16} data-id="element-1153" />
                                      </button>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle delete functionality here
                              if (confirm('Are you sure you want to delete this transcript resource?')) {
                                alert('Transcript resource deleted');
                              }
                            }} className="mr-2 text-gray-400 hover:text-red-500" aria-label="Delete transcript resource" data-id="element-1154">
                                        <Trash2 size={16} data-id="element-1155" />
                                      </button>
                                    </>}
                                  <ExternalLink size={16} className="text-gray-400" data-id="element-1156" />
                                </div>
                              </a>
                              <a href={module.slidesUrl} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:shadow-sm" data-id="element-1157">
                                <Presentation size={20} className="text-amber-500 mr-3" data-id="element-1158" />
                                <div data-id="element-1159">
                                  <div className="text-sm font-medium text-gray-800" data-id="element-1160">
                                    Download Slides
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-1161">
                                    Presentation slides for reference
                                  </div>
                                </div>
                                <div className="ml-auto flex items-center" data-id="element-1162">
                                  {userRole === 'Program Manager' && <>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle edit functionality here
                              alert('Edit slides URL: ' + module.slidesUrl);
                            }} className="mr-2 text-gray-400 hover:text-blue-500" aria-label="Edit slides URL" data-id="element-1163">
                                        <Edit size={16} data-id="element-1164" />
                                      </button>
                                      <button onClick={e => {
                              e.preventDefault();
                              // Handle delete functionality here
                              if (confirm('Are you sure you want to delete this slides resource?')) {
                                alert('Slides resource deleted');
                              }
                            }} className="mr-2 text-gray-400 hover:text-red-500" aria-label="Delete slides resource" data-id="element-1165">
                                        <Trash2 size={16} data-id="element-1166" />
                                      </button>
                                    </>}
                                  <Download size={16} className="text-gray-400" data-id="element-1167" />
                                </div>
                              </a>
                              {userRole === 'Program Manager' && <button onClick={e => {
                        e.preventDefault();
                        // Handle add new resource functionality
                        alert('Add new resource to module: ' + module.title);
                      }} className="flex items-center p-3 bg-gray-50 border border-dashed border-gray-300 rounded-md hover:border-blue-300 hover:bg-gray-100 w-full justify-center mt-2" data-id="element-1168">
                                  <Plus size={16} className="mr-2 text-gray-500" data-id="element-1169" />
                                  <span className="text-sm text-gray-600" data-id="element-1170">
                                    Add New Resource
                                  </span>
                                </button>}
                            </div>
                          </div>
                          {/* Right Column - Templates */}
                          <div data-id="element-1171">
                            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1172">
                              Templates & Tools
                            </h4>
                            <div className="space-y-3" data-id="element-1173">
                              {module.templates.map(template => <a key={template.id} href={template.url} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:shadow-sm" data-id="element-1174">
                                  {getTemplateIcon(template.type)}
                                  <div className="ml-3" data-id="element-1175">
                                    <div className="text-sm font-medium text-gray-800" data-id="element-1176">
                                      {template.name}
                                    </div>
                                    <div className="text-xs text-gray-500 capitalize" data-id="element-1177">
                                      {template.type === 'agent' ? 'AI Agent' : template.type}
                                    </div>
                                  </div>
                                  <div className="ml-auto flex items-center" data-id="element-1178">
                                    {userRole === 'Program Manager' && <button onClick={e => {
                            e.preventDefault();
                            setSelectedTemplate(template);
                            setSelectedModule(module);
                            setIsEditTemplateModalOpen(true);
                          }} className="mr-2 text-gray-400 hover:text-blue-500" aria-label="Edit template" data-id="element-1179">
                                        <Edit size={16} data-id="element-1180" />
                                      </button>}
                                    {template.type === 'agent' ? <Link to={template.url} className="ml-auto" data-id="element-1181">
                                        <ChevronRight size={16} className="text-gray-400" data-id="element-1182" />
                                      </Link> : <ExternalLink size={16} className="text-gray-400" data-id="element-1183" />}
                                  </div>
                                </a>)}
                              {userRole === 'Program Manager' && <button onClick={e => {
                        e.preventDefault();
                        // Handle add new template functionality
                        alert('Add new template to module: ' + module.title);
                      }} className="flex items-center p-3 bg-gray-50 border border-dashed border-gray-300 rounded-md hover:border-blue-300 hover:bg-gray-100 w-full justify-center mt-2" data-id="element-1184">
                                  <Plus size={16} className="mr-2 text-gray-500" data-id="element-1185" />
                                  <span className="text-sm text-gray-600" data-id="element-1186">
                                    Add New Template
                                  </span>
                                </button>}
                            </div>
                          </div>
                        </div>
                        {/* Status Update Button - Only for Founders */}
                        {userRole === 'Founder' && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1187">
                            <div className="flex items-center justify-between" data-id="element-1188">
                              <div data-id="element-1189">
                                <h4 className="text-sm font-medium text-gray-700" data-id="element-1190">
                                  Module Status
                                </h4>
                                <p className="text-xs text-gray-500" data-id="element-1191">
                                  Update your progress on this module
                                </p>
                              </div>
                              <div className="flex space-x-2" data-id="element-1192">
                                <button className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${module.status === 'not-started' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1193">
                                  <Circle size={14} className="mr-1.5" data-id="element-1194" />
                                  Not Started
                                </button>
                                <button className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${module.status === 'in-progress' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1195">
                                  <Clock size={14} className="mr-1.5" data-id="element-1196" />
                                  In Progress
                                </button>
                                <button className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${module.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1197">
                                  <CheckCircle size={14} className="mr-1.5" data-id="element-1198" />
                                  Completed
                                </button>
                              </div>
                            </div>
                          </div>}
                      </div>}
                  </div>)}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex justify-center items-center py-6" data-id="element-1199">
          {userRole === 'Program Manager' && <button onClick={() => handleAddModule(currentPhase.id)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center transition-colors" data-id="element-1200">
              <Plus size={16} className="mr-2" data-id="element-1201" /> Add Module
            </button>}
        </div>
      </div>
      {/* Add Module Modal - Only for Program Manager */}
      {isAddModuleModalOpen && userRole === 'Program Manager' && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1202">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl" data-id="element-1203">
            <div className="flex items-center justify-between mb-4" data-id="element-1204">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-1205">
                Add New Module
              </h3>
              <button onClick={() => setIsAddModuleModalOpen(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1206">
                <X size={20} data-id="element-1207" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1208">
              {/* Modal content - form fields for adding a module */}
              <div data-id="element-1209">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1210">
                  Module Title
                </label>
                <input type="text" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter module title" data-id="element-1211" />
              </div>
              <div data-id="element-1212">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1213">
                  Module Description
                </label>
                <textarea className="w-full p-2 border border-gray-300 rounded-md" rows={3} placeholder="Enter module description" data-id="element-1214" />
              </div>
              <div data-id="element-1215">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1216">
                  Module Order
                </label>
                <input type="number" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter module order" min={1} data-id="element-1217" />
              </div>
              <div className="pt-4 flex justify-end space-x-3" data-id="element-1218">
                <button onClick={() => setIsAddModuleModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1219">
                  Cancel
                </button>
                <button onClick={() => setIsAddModuleModalOpen(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1220">
                  Add Module
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Edit Module Modal - Only for Program Manager */}
      {isEditModuleModalOpen && selectedModule && userRole === 'Program Manager' && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1221">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl" data-id="element-1222">
              <div className="flex items-center justify-between mb-4" data-id="element-1223">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1224">
                  Edit Module
                </h3>
                <button onClick={() => setIsEditModuleModalOpen(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1225">
                  <X size={20} data-id="element-1226" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-1227">
                {/* Modal content - form fields for editing a module */}
                <div data-id="element-1228">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1229">
                    Module Title
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedModule.title} data-id="element-1230" />
                </div>
                <div data-id="element-1231">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1232">
                    Module Description
                  </label>
                  <textarea className="w-full p-2 border border-gray-300 rounded-md" rows={3} defaultValue={selectedModule.description} data-id="element-1233" />
                </div>
                <div className="pt-4 flex justify-between items-center" data-id="element-1234">
                  <button onClick={() => {
              if (window.confirm('Are you sure you want to delete this module? This action cannot be undone.')) {
                handleDeleteModule(currentPhase.id, selectedModule.id);
                setIsEditModuleModalOpen(false);
              }
            }} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center" data-id="element-1235">
                    <Trash2 size={16} className="mr-2" data-id="element-1236" /> Delete Module
                  </button>
                  <div className="flex space-x-3" data-id="element-1237">
                    <button onClick={() => setIsEditModuleModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1238">
                      Cancel
                    </button>
                    <button onClick={() => setIsEditModuleModalOpen(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1239">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      {/* Edit Template Modal - Only for Program Manager */}
      {isEditTemplateModalOpen && selectedTemplate && selectedModule && userRole === 'Program Manager' && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1240">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl" data-id="element-1241">
              <div className="flex items-center justify-between mb-4" data-id="element-1242">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-1243">
                  Edit Template
                </h3>
                <button onClick={() => setIsEditTemplateModalOpen(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1244">
                  <X size={20} data-id="element-1245" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-1246">
                <div data-id="element-1247">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1248">
                    Template Name
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedTemplate.name} data-id="element-1249" />
                </div>
                <div data-id="element-1250">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1251">
                    Template Type
                  </label>
                  <select className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedTemplate.type} data-id="element-1252">
                    <option value="miro" data-id="element-1253">Miro</option>
                    <option value="powerpoint" data-id="element-1254">PowerPoint</option>
                    <option value="word" data-id="element-1255">Word</option>
                    <option value="excel" data-id="element-1256">Excel</option>
                    <option value="agent" data-id="element-1257">AI Agent</option>
                  </select>
                </div>
                <div data-id="element-1258">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1259">
                    Template URL
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={selectedTemplate.url} data-id="element-1260" />
                </div>
                <div className="pt-4 flex justify-between items-center" data-id="element-1261">
                  <button onClick={() => {
              if (window.confirm(`Are you sure you want to delete the template "${selectedTemplate.name}"? This action cannot be undone.`)) {
                handleDeleteTemplate(selectedModule.id, selectedTemplate.id);
                setIsEditTemplateModalOpen(false);
              }
            }} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center" data-id="element-1262">
                    <Trash2 size={16} className="mr-2" data-id="element-1263" /> Delete Template
                  </button>
                  <div className="flex space-x-3" data-id="element-1264">
                    <button onClick={() => setIsEditTemplateModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1265">
                      Cancel
                    </button>
                    <button onClick={() => setIsEditTemplateModalOpen(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1266">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
    </div>;
};
export default Program;